import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { scaleIn400ms } from '../../../../../../../@vex/animations/scale-in.animation';
import { fadeInRight400ms } from '../../../../../../../@vex/animations/fade-in-right.animation';
import { stagger40ms } from '../../../../../../../@vex/animations/stagger.animation';
import { fadeInUp400ms } from '../../../../../../../@vex/animations/fade-in-up.animation';
import { scaleFadeIn400ms } from '../../../../../../../@vex/animations/scale-fade-in.animation';
import {
  ContractDeliveryCostEnum,
  ContractDeliveryTimeEnum,
  ContractTypeOfRevisionEnum,
  ContractValidityEnum,
} from '../../../../../../core/enums/contract.enum';
import { Observable, Subscription, switchMap, tap } from 'rxjs';
import { ContractService } from '../../contract.service';
import {
  RecurrenceEnum,
  recurrenceEnumTranslation,
  recurrenceEnumTranslationOccurence,
} from '../../../../../../core/enums/recurrence.enum';
import { MonthEnum, monthEnumTranslation } from '../../../../../../core/enums/month.enum';
import { DayEnum, dayEnumTranslation } from '../../../../../../core/enums/day.enum';
import moment from 'moment';
import { DialogConfirmComponent } from '../../../../../../../@vex/components/dialog-confirm/dialog-confirm.component';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { InputTypeInterface } from '../../../../../../core/base/interfaces/input-type.interface';
import { ContractStatusEnum } from '../../../../../../core/enums/contract-status.enum';
import { GenericTvaService } from '../../../../settings/generic-tva/generic-tva.service';
import { GenericTVAModel } from '../../../../../../core/models/generic-tva.model';
import { CustomerGroupService } from '../../../../settings/customer-group/customer-group.service';
import { GenericTypeAdditionalCostModel } from '../../../../../../core/models/generic-type-additional-cost.model';
import { GenericEngagementFormatEnum } from '../../../../../../core/enums/generic-engagement-format.enum';
import { GenericAdditionalCostService } from '../../../../settings/generic-additional-cost/generic-additional-cost.service';
import { ActivatedRoute, Router } from '@angular/router';
import { GenericAdditionalCostModel } from '../../../../../../core/models/generic-additional-cost.model';
import { ContractSustainabilityPriceEnum } from '../../../../../../core/enums/contract-sustainability-price.enum';
import {
  ContractAdditionalCostsModel,
  ContractDeliveryCostModel,
  ContractDeliveryTimeModel,
  ContractInterventionTimeModel,
  ContractModel,
  ContractSustainabilityAndPriceTransModel,
  RecurringBasketModel,
} from '../../../../../../core/models/contract.model';
import { debounceTime, startWith, take, map } from 'rxjs/operators';
import { quillConfig } from '../../../../../../core/config/quill.config';
import { GenericAdditionalCostTypeEnum } from '../../../../../../core/enums/generic-additional-cost-type.enum';
import {
  convertMinutesToUnit,
  TimeDelay,
  TimeUnit,
  translateTimeDelay,
  translateTimeUnitObject,
} from '../../../../../../core/utils/time-utils';
import { AuthService } from '../../../../../../core/services/auth.service';
import { ENUM_PERMISSIONS } from '../../../../../../core/enums/permission.enum';
import { lockFormWhenReadOnly } from 'src/app/core/utils/form-permission.util';

@Component({
  selector: 'vex-contract-details-cga',
  templateUrl: './contract-details-cga.component.html',
  styleUrls: [
    './contract-details-cga.component.scss',
    '../../../../../../../../node_modules/quill/dist/quill.snow.css',
    '../../../../../../../@vex/styles/partials/plugins/_quill.scss',
  ],
  animations: [scaleIn400ms, fadeInRight400ms, stagger40ms, fadeInUp400ms, scaleFadeIn400ms],
  encapsulation: ViewEncapsulation.None,
})
export class ContractDetailsCgaComponent implements OnInit {
  readonly ENUM_PERMISSIONS = ENUM_PERMISSIONS;
  /** PRED-3230 : sans UPDATE_CONTRACT, les CGA sont consultables mais jamais éditables. */
  isUpdateDisabledPermission = !this.authService.hasPermission(ENUM_PERMISSIONS.UPDATE_CONTRACT);

  constructor(
    public contractService: ContractService,
    public customerGroupService: CustomerGroupService,
    private genericAdditionalCostService: GenericAdditionalCostService,
    private dialog: MatDialog,
    private router: Router,
    private genericTvaService: GenericTvaService,
    public authService: AuthService,
  ) {}

  private id: string;
  private defaultOtherExpenses: GenericTypeAdditionalCostModel;
  public genericAdditionalCost$: Observable<GenericAdditionalCostModel[]>;
  public contractSustainabilityPrice = Object.values(ContractSustainabilityPriceEnum);
  public contractTypeOfRevision = Object.values(ContractTypeOfRevisionEnum);
  public form: UntypedFormGroup;
  public sustainabilityAndPriceTransForm: UntypedFormGroup;
  public priceRevisionForm: UntypedFormGroup;
  public additionalCostsForm: UntypedFormArray;
  public additionalCostAddForm: UntypedFormGroup;
  public deliveryCostForm: UntypedFormGroup;
  public deliveryTimeForm: UntypedFormGroup;
  public interventionTimeForm: UntypedFormGroup;
  public contractSustainabilityPriceEnum = ContractSustainabilityPriceEnum;
  public contractTypeOfRevisionEnum = ContractTypeOfRevisionEnum;
  public deliveryCostsEnum = ContractDeliveryCostEnum;
  monthEnum = Object.values(MonthEnum).map(month => monthEnumTranslation(month));
  dayEnum = Object.values(DayEnum).map(day => dayEnumTranslation(day));
  public deliveryCostEnum = Object.values(ContractDeliveryCostEnum);
  public availableGenericAdditionalCosts: GenericAdditionalCostModel[];
  defaultTimeDelay = { quantity: 0, displayedUnit: TimeUnit.HOUR }; // Par défaut : 0 et une unité par défaut

  public displayRepeat = true;
  public repeatLabel: string;
  autoCompleteDisplayProperty: string = null;

  public anniversaryCheckboxActive: boolean;

  recurrenceEnum = Object.values(RecurrenceEnum).map(recurrence => recurrenceEnumTranslation(recurrence));

  public autoCompleteValue: Array<any>;
  autoCompleteType: typeof InputTypeInterface;
  public autoCompleteIsActive = false;
  public autocompleteMonth = false;

  public quillConfig = quillConfig;
  private subscription = new Subscription();
  public contract: ContractModel;
  public ContractStatusEnum = ContractStatusEnum;
  readonly TimeUnit = TimeUnit;
  public hasPrestation = false;
  // Terme de recherche courant pour rafraîchir après ajout/suppression
  private genericAdditionalCostSearchTerm: string = '';

  ngOnInit(): void {
    this.initData();

    this.contractService.entity$.pipe(take(1)).subscribe(contract => {
      if (contract) {
        this.id = contract._id;
        this.contract = contract;
        this.hasPrestation = this.contractService.hasPrestation(contract);

        // Initialisation des formulaires avec les données de `contract`
        this.initSustainabilityAndPriceTransForm(this.contract.sustainabilityAndPriceTrans);
        this.initPriceRevisionForm(this.contract.priceRevision);
        this.initAdditionalCostForms(this.contract.additionalCosts);
        this.initDeliveryCostForm(this.contract.deliveryCost);
        this.initDeliveryTimeForm(this.contract.deliveryTime);
        this.initInterventionTimeForm(this.contract.interventionTime);

        // Initialiser l'autocomplete (mode recherche serveur)
        this.genericAdditionalCostAutocompleteChange('');

        // const defaultDeliveryTime =
        //     this.contract?.deliveryTime?.deliveryTime ||
        // ContractDeliveryTimeEnum.URGENT_THEORETICAL_DELIVERY_TIME;

        // this.deliveryCostForm.get('deliveryTime').setValue(defaultDeliveryTime);

        // Création du formulaire principal
        this.initMainForm();
        lockFormWhenReadOnly(this.form, this.isUpdateDisabledPermission);
        // Le formulaire d'ajout d'un frais annexe vit hors du formulaire principal.
        lockFormWhenReadOnly(this.additionalCostAddForm, this.isUpdateDisabledPermission);
      }
    });

    this.contractService.entity$.subscribe(contract => {
      this.contract = contract;
    });

    if (this.isContractIsInternal) {
      this.sustainabilityAndPriceTransForm.get('sustainabilityPrice').setValue(ContractValidityEnum.FIRM);
    }
  }

  private theoreticalTimeLabel(prefix: 'Delivery' | 'Intervention', kind: 'urgent' | 'standard'): string {
    const groupName = prefix === 'Delivery' ? 'deliveryTime' : 'interventionTime';
    const controlName = `${kind}Theoretical${prefix}Value`;
    const noun = prefix === 'Delivery' ? 'livraison' : 'intervention';
    const apostrophe = prefix === 'Delivery' ? 'de ' : "d'";
    const baseLabel = `Délai ${apostrophe}${noun} théorique ${kind}`;

    const value: TimeDelay = this.form.controls[groupName].get(controlName)?.value;
    return value ? `${baseLabel} (en ${translateTimeDelay(value).label})` : baseLabel;
  }

  private urgentLessOrEqualStandard =
    (prefix: 'Delivery' | 'Intervention'): ValidatorFn =>
    (group: AbstractControl): ValidationErrors | null => {
      const urgent = group.get(`urgentTheoretical${prefix}Value`)?.value as TimeDelay | null;
      const standard = group.get(`standardTheoretical${prefix}Value`)?.value as TimeDelay | null;
      if (!urgent?.quantity || !standard?.quantity) return null;
      return urgent.quantity > standard.quantity ? { urgentExceedsStandard: true } : null;
    };

  getUrgentTheoreticalDeliveryValueLabel(): string {
    return this.theoreticalTimeLabel('Delivery', 'urgent');
  }

  getStandardTheoreticalDeliveryValueLabel(): string {
    return this.theoreticalTimeLabel('Delivery', 'standard');
  }

  getUrgentTheoreticalInterventionValueLabel(): string {
    return this.theoreticalTimeLabel('Intervention', 'urgent');
  }

  getStandardTheoreticalInterventionValueLabel(): string {
    return this.theoreticalTimeLabel('Intervention', 'standard');
  }

  // Exemple de méthode dans le composant parent
  getDefaultValue(controlName: string, form: UntypedFormGroup = this.deliveryTimeForm): TimeDelay {
    const controlValue = form.get(controlName)?.value;
    return controlValue ? controlValue : this.defaultTimeDelay;
  }

  async initData() {
    this.getDefaultOtherExpensesId();
  }

  genericAdditionalCostAutocompleteChange(search: any = '') {
    // Simple: 1 appel au backend par saisie ou action (ajout/suppression)
    const term = typeof search === 'string' ? search : '';
    this.genericAdditionalCostSearchTerm = term;

    // IDs déjà présents dans le formulaire (inclut ceux du contrat via init)
    const selectedIds = new Set<string>(
      (this.additionalCostsForm?.controls || []).map(ctrl => ctrl.get('additionalCost')?.value?._id).filter(id => !!id),
    );

    this.genericAdditionalCost$ = this.genericAdditionalCostService
      .findAll(null, 10000, 'label', 'asc', term, { requiredFields: ['*'] })
      .pipe(
        map(x =>
          (x.data || [])
            .filter((item: GenericAdditionalCostModel) => !item.isInactive)
            .filter((item: GenericAdditionalCostModel) => !selectedIds.has(item._id))
            .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true })),
        ),
      );
  }

  showCustomAdditionalCosts(additionalCost: GenericAdditionalCostModel) {
    if (additionalCost.typeAdditionalCosts) {
      return `[${additionalCost.typeAdditionalCosts.label} - ${additionalCost.type}]`;
    }
  }

  computeTva(value: number, tva: GenericTVAModel) {
    let rate: number;

    if (tva && tva.rateList && tva.rateList.length > 0) {
      rate = this.genericTvaService.getCurrentRate(tva.rateList).rate;
      return value * (1 + rate / 100);
    } else {
      return value;
    }
  }

  initMainForm() {
    this.form = new UntypedFormGroup({
      sustainabilityAndPriceTrans: this.sustainabilityAndPriceTransForm,
      priceRevision: this.priceRevisionForm,
      additionalCosts: this.additionalCostsForm,
      deliveryCost: this.deliveryCostForm,
      specialConditions: new UntypedFormControl(this.contract ? this.contract.specialConditions : null),
      deliveryTime: this.deliveryTimeForm,
      interventionTime: this.interventionTimeForm,
    });

    this.subscription.add(
      this.form.valueChanges
        .pipe(
          debounceTime(500),
          switchMap(value => {
            const rawValue = this.form.getRawValue();
            return this.contractService.updateCga(this.id, rawValue);
          }),
        )
        .subscribe(res => {}),
    );
  }

  initSustainabilityAndPriceTransForm(data: ContractSustainabilityAndPriceTransModel = null) {
    this.sustainabilityAndPriceTransForm = new UntypedFormGroup({
      sustainabilityPrice: new UntypedFormControl(data ? data.sustainabilityPrice : null, Validators.required),
      typeOfRevision: new UntypedFormControl(data ? data.typeOfRevision : null),
      globalRevision: new UntypedFormControl(data ? data.globalRevision : null),
      revisionAmount: new UntypedFormControl(data ? data.revisionAmount : null),
      revisionForm: new UntypedFormControl(data ? data.revisionForm : null),
    });

    const isQuote = this.contract.genericEngagement.format === GenericEngagementFormatEnum.QUOTE;
    const isReviewable =
      this.sustainabilityAndPriceTransForm.get('sustainabilityPrice').value ===
      ContractSustainabilityPriceEnum.REVIEWABLE;

    if (isQuote && isReviewable) {
      this.sustainabilityAndPriceTransForm
        .get('typeOfRevision')
        .setValue(ContractTypeOfRevisionEnum.FIXED, { emitEvent: false });
      this.sustainabilityAndPriceTransForm.get('typeOfRevision').disable({ emitEvent: false });
    }

    if (!isQuote && isReviewable && !data.typeOfRevision) {
      this.sustainabilityAndPriceTransForm.get('typeOfRevision').setValue(ContractTypeOfRevisionEnum.FIXED);
    }

    this.sustainabilityAndPriceTransFormValueChanges();
  }

  get isUserCurrentEngagementContact(): boolean {
    return this.contract?.engagementContact?._id === this.authService.currentUserValue()?._id;
  }

  get isContractIsInternal(): boolean {
    return !!this.contract?.supportEstablishment;
  }

  initPriceRevisionForm(data: RecurringBasketModel = null) {
    this.priceRevisionForm = new UntypedFormGroup({
      endDate: new UntypedFormControl(),
      startDate: new UntypedFormControl(),
      recurrence: new UntypedFormControl(this.recurrenceEnum.find(x => x.value === data?.recurrence) || null, [
        Validators.required,
      ]),
      repeat: new UntypedFormControl(data ? data.repeat : null),
      activated: new UntypedFormControl(data ? data.activated : null),
      month: new UntypedFormControl(data ? this.monthEnum.find(x => x.value === data?.month) || null : null),
      isAnniversaryDate: new UntypedFormControl(data ? data.isAnniversaryDate : true),
    });

    this.priceRevisionForm.patchValue({
      startDate: this.contract.startValidityDate,
      endDate: this.contract.endValidityDate,
    });

    if (data && data.recurrence) {
      this.setRepeat(data.recurrence);
    }

    // disable all fields if contract.sustainabilityAndPriceTrans.sustainabilityPrice === ContractSustainabilityPriceEnum.FIRM
    if (
      this.sustainabilityAndPriceTransForm.get('sustainabilityPrice').value === ContractSustainabilityPriceEnum.FIRM
    ) {
      this.priceRevisionForm.disable();
    } else {
      this.priceRevisionForm.enable();
    }
    this.priceRevisionFormValueChanges();
  }

  initDeliveryCostForm(data: ContractDeliveryCostModel) {
    this.deliveryCostForm = new UntypedFormGroup({
      deliveryCost: new UntypedFormControl(data ? data.deliveryCost : null, Validators.required),
      fromValue: new UntypedFormControl(data ? data.fromValue : null, Validators.required),
      packageValue: new UntypedFormControl(data ? data.packageValue : null, Validators.required),
    });

    if (data && data.deliveryCost) {
      if (data.deliveryCost === ContractDeliveryCostEnum.FREE_SHIPPING) {
        this.deliveryCostForm.get('fromValue').disable();
        this.deliveryCostForm.get('packageValue').disable();
      }

      if (data.deliveryCost === ContractDeliveryCostEnum.FREE_SHIPPING_FROM) {
        this.deliveryCostForm.get('fromValue').enable();
        this.deliveryCostForm.get('packageValue').enable();
      }

      if (data.deliveryCost === ContractDeliveryCostEnum.PACKAGE) {
        this.deliveryCostForm.get('fromValue').disable();
        this.deliveryCostForm.get('packageValue').enable();
      }

      if (data.deliveryCost === ContractDeliveryCostEnum.NOT_DEFINED) {
        this.deliveryCostForm.get('fromValue').disable();
        this.deliveryCostForm.get('packageValue').disable();
      }
    }
    this.deliveryCostFormValueChanges();
  }

  initDeliveryTimeForm(data: ContractDeliveryTimeModel) {
    this.deliveryTimeForm = new UntypedFormGroup(
      {
        standardTheoreticalDeliveryValue: new UntypedFormControl(
          data && data.standardTheoreticalDeliveryValue ? data.standardTheoreticalDeliveryValue : this.defaultTimeDelay,
        ),
        urgentTheoreticalDeliveryValue: new UntypedFormControl(
          data && data.urgentTheoreticalDeliveryValue ? data.urgentTheoreticalDeliveryValue : this.defaultTimeDelay,
        ),
      },
      { validators: this.urgentLessOrEqualStandard('Delivery') },
    );

    this.deliveryTimeForm.updateValueAndValidity();
  }

  initInterventionTimeForm(data: ContractInterventionTimeModel) {
    this.interventionTimeForm = new UntypedFormGroup(
      {
        standardTheoreticalInterventionValue: new UntypedFormControl(
          data && data.standardTheoreticalInterventionValue
            ? data.standardTheoreticalInterventionValue
            : this.defaultTimeDelay,
        ),
        urgentTheoreticalInterventionValue: new UntypedFormControl(
          data && data.urgentTheoreticalInterventionValue
            ? data.urgentTheoreticalInterventionValue
            : this.defaultTimeDelay,
        ),
      },
      { validators: this.urgentLessOrEqualStandard('Intervention') },
    );

    this.interventionTimeForm.updateValueAndValidity();
  }

  initAdditionalCostForms(data: ContractAdditionalCostsModel[] = []) {
    this.additionalCostsForm = new UntypedFormArray([]);
    this.additionalCostAddForm = this.createAdditionalCostControl();
    data.forEach((x: ContractAdditionalCostsModel, index: number) => {
      const formGroup = this.createAdditionalCostFormGroup(x);
      this.additionalCostsForm.insert(index, formGroup);
    });

    // Pas d'abonnement à valueChanges ici pour éviter les appels multiples
  }

  createAdditionalCostFormGroup(costsModel: ContractAdditionalCostsModel): UntypedFormGroup {
    const isFieldRequired = costsModel.additionalCost.type !== GenericAdditionalCostTypeEnum.TO_DEFINED;

    const formGroup = new UntypedFormGroup({
      additionalCost: new UntypedFormControl(costsModel.additionalCost, Validators.required),
      numValueHT: new UntypedFormControl(costsModel.numValueHT, isFieldRequired ? Validators.required : null),
      numValueTTC: new UntypedFormControl(costsModel.numValueTTC, isFieldRequired ? Validators.required : null),
      additionalInformation: new UntypedFormControl(costsModel.additionalInformation),
      type: new UntypedFormControl({ value: costsModel.type, disabled: true }),
    });

    formGroup.controls.numValueHT.valueChanges.subscribe(value => {
      formGroup.controls.numValueTTC.setValue(this.computeTva(value, costsModel.additionalCost.tva));
    });

    return formGroup;
  }

  // Par exemple, vous pouvez créer une méthode privée pour mettre à jour les validateurs
  private updateValidatorsForNumValues(
    additionalCostModel: GenericAdditionalCostModel,
    numValueHTControl: AbstractControl,
    numValueTTCControl: AbstractControl,
  ): void {
    // Si le type est TO_DEFINED, les champs ne sont pas requis
    if (additionalCostModel.type === GenericAdditionalCostTypeEnum.TO_DEFINED) {
      numValueHTControl.clearValidators();
      numValueTTCControl.clearValidators();
    } else {
      numValueHTControl.setValidators(Validators.required);
      numValueTTCControl.setValidators(Validators.required);
    }
    numValueHTControl.updateValueAndValidity({ emitEvent: false });
    numValueTTCControl.updateValueAndValidity({ emitEvent: false });
  }

  createAdditionalCostControl(): UntypedFormGroup {
    const formGroup = new UntypedFormGroup({
      additionalCost: new UntypedFormControl(null),
      numValueHT: new UntypedFormControl({ value: null, disabled: true }, Validators.required),
      numValueTTC: new UntypedFormControl({ value: null, disabled: true }, Validators.required),
      additionalInformation: new UntypedFormControl({ value: null, disabled: true }),
    });

    return formGroup;
  }

  onAdditionalCostSelected(eventOrValue: any) {
    if (this.isUpdateDisabledPermission) {
      return;
    }
    const selectedValue: GenericAdditionalCostModel = eventOrValue?.option ? eventOrValue.option.value : eventOrValue;
    if (!selectedValue) {
      return;
    }

    // Récupération des contrôles du formulaire d'ajout
    const numValueHTControl = this.additionalCostAddForm.get('numValueHT');
    const numValueTTCControl = this.additionalCostAddForm.get('numValueTTC');
    const additionalInformationControl = this.additionalCostAddForm.get('additionalInformation');

    // Active les contrôles désactivés
    numValueHTControl.enable({ emitEvent: false });
    numValueTTCControl.enable({ emitEvent: false });
    additionalInformationControl.enable({ emitEvent: false });

    // Met à jour les validateurs en fonction du type
    this.updateValidatorsForNumValues(selectedValue, numValueHTControl, numValueTTCControl);

    // Abonnement pour mettre à jour numValueTTC lorsque numValueHT change
    const numValueHTSubscription = numValueHTControl.valueChanges.subscribe((htValue: number) => {
      // On calcule la valeur TTC en fonction du HT et du taux de TVA du modèle
      const ttcValue = htValue != null ? this.computeTva(htValue, selectedValue.tva) : null;
      numValueTTCControl.setValue(ttcValue, { emitEvent: false });
    });

    // Ajoute le formGroup créé à la collection/formArray de coûts additionnels
    this.additionalCostsForm.insert(0, this.additionalCostAddForm);

    // Crée un nouveau formGroup pour l'ajout d'un autre coût additionnel
    this.additionalCostAddForm = this.createAdditionalCostControl();
    this.additionalCostAddForm.get('numValueHT').disable({ emitEvent: false });
    this.additionalCostAddForm.get('numValueTTC').disable({ emitEvent: false });
    this.additionalCostAddForm.get('additionalInformation').disable({ emitEvent: false });

    // Met à jour la liste des coûts disponibles en retirant celui déjà sélectionné
    if (this.availableGenericAdditionalCosts?.length) {
      this.availableGenericAdditionalCosts = this.availableGenericAdditionalCosts.filter(
        x => x._id !== selectedValue._id,
      );
    }

    // Après une sélection, réinitialiser la recherche pour que l'ouverture suivante affiche la liste complète filtrée
    this.genericAdditionalCostSearchTerm = '';
    this.genericAdditionalCostAutocompleteChange('');
  }

  removeAdditionalCostControl(index: number) {
    const additionalCost = this.additionalCostsForm.at(index).get('additionalCost').value;

    this.additionalCostsForm.removeAt(index);
    // get the additionalCost from the formArray at index and push it to availableGenericAdditionalCosts
    if (this.availableGenericAdditionalCosts) {
      this.availableGenericAdditionalCosts.push(additionalCost);
      this.availableGenericAdditionalCosts.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
    }

    // Après suppression, réinitialiser la recherche pour afficher la liste complète filtrée
    this.genericAdditionalCostSearchTerm = '';
    this.genericAdditionalCostAutocompleteChange('');
  }

  deliveryCostFormValueChanges() {
    this.subscription.add(
      this.deliveryCostForm.get('deliveryCost').valueChanges.subscribe(value => {
        this.deliveryCostForm.get('fromValue').setValue(null);
        this.deliveryCostForm.get('packageValue').setValue(null);

        if (value === ContractDeliveryCostEnum.FREE_SHIPPING) {
          this.deliveryCostForm.get('fromValue').disable();
          this.deliveryCostForm.get('packageValue').disable();
        }

        if (value === ContractDeliveryCostEnum.FREE_SHIPPING_FROM) {
          this.deliveryCostForm.get('fromValue').enable();
          this.deliveryCostForm.get('packageValue').enable();
        }

        if (value === ContractDeliveryCostEnum.PACKAGE) {
          this.deliveryCostForm.get('fromValue').disable();
          this.deliveryCostForm.get('packageValue').enable();
        }

        if (value === ContractDeliveryCostEnum.NOT_DEFINED) {
          this.deliveryCostForm.get('fromValue').disable();
          this.deliveryCostForm.get('packageValue').disable();
        }
      }),
    );
    this.subscription.add(
      this.deliveryCostForm.get('fromValue').valueChanges.subscribe(value => {
        if (value === 0) {
          this.deliveryCostForm.get('packageValue').disable();
          this.deliveryCostForm.get('packageValue').setValue(null);
        }
      }),
    );
  }

  priceRevisionFormValueChanges() {
    this.subscription.add(
      this.priceRevisionForm.get('recurrence').valueChanges.subscribe(value => {
        if (value) {
          this.setForm(value);
        }
      }),
    );
  }

  private toggleFormField(fieldName: string, enable: boolean, validators = []) {
    const field = this.sustainabilityAndPriceTransForm.get(fieldName);
    if (enable) {
      field.enable();
      field.setValidators(validators);
    } else {
      field.disable();
      field.setValue(null);
      field.setValidators(null);
    }
    field.updateValueAndValidity();
  }

  sustainabilityAndPriceTransFormValueChanges() {
    this.subscription.add(
      this.sustainabilityAndPriceTransForm
        .get('sustainabilityPrice')
        .valueChanges.pipe(
          tap(value => {
            const isQuote = this.contract.genericEngagement.format === GenericEngagementFormatEnum.QUOTE;
            const isReviewable = value === ContractSustainabilityPriceEnum.REVIEWABLE;
            const shouldEnable = isReviewable;

            if (!isReviewable) {
              // clear all priceRevisionForm fields
              this.priceRevisionForm.reset();
            }

            this.priceRevisionForm[shouldEnable ? 'enable' : 'disable']();
            this.toggleFormField('typeOfRevision', shouldEnable, [Validators.required]);

            if (isReviewable && isQuote) {
              this.sustainabilityAndPriceTransForm.get('typeOfRevision').disable();
              this.sustainabilityAndPriceTransForm.get('typeOfRevision').setValue(ContractTypeOfRevisionEnum.FIXED);
            }
          }),
        )
        .subscribe(),
    );

    this.subscription.add(
      this.sustainabilityAndPriceTransForm
        .get('typeOfRevision')
        .valueChanges.pipe(
          startWith(this.sustainabilityAndPriceTransForm.get('typeOfRevision').value),
          tap(value => {
            // By default, globalRevision is true if typeOfRevision is FIXED and creationStep is 3
            if (
              this.sustainabilityAndPriceTransForm.get('typeOfRevision').value === ContractTypeOfRevisionEnum.FIXED &&
              this.contract.creationStep === 3
            ) {
              this.sustainabilityAndPriceTransForm.get('globalRevision').setValue(true);
            }

            switch (value) {
              case ContractTypeOfRevisionEnum.FIXED:
                this.toggleFormField('globalRevision', true);
                this.toggleFormField('revisionForm', false);
                break;
              case ContractTypeOfRevisionEnum.VARIABLE:
                this.toggleFormField('revisionForm', true, [Validators.required]);
                this.toggleFormField('globalRevision', false);
                break;
              default:
                this.toggleFormField('globalRevision', false);
                this.toggleFormField('revisionForm', false);
                break;
            }
          }),
        )
        .subscribe(),
    );

    this.subscription.add(
      this.sustainabilityAndPriceTransForm
        .get('globalRevision')
        .valueChanges.pipe(
          startWith(this.sustainabilityAndPriceTransForm.get('globalRevision').value),
          tap(value => {
            const shouldEnable = !!value;
            this.toggleFormField('revisionAmount', shouldEnable, [Validators.required]);
          }),
        )
        .subscribe(),
    );
  }

  setRepeat(value: RecurrenceEnum) {
    const startDate = moment(this.contract.startValidityDate);
    const dayOfMonth: number = startDate.date();
    const monthNumber: number = startDate.month();

    switch (value) {
      case RecurrenceEnum.Weekly:
        this.repeatLabel = 'Semaine(s)';
        this.autoCompleteValue = this.dayEnum;
        this.autoCompleteType = 'multiple';
        this.autoCompleteDisplayProperty = 'label';
        this.autoCompleteIsActive = true;
        this.anniversaryCheckboxActive = false;

        // get index of day enum

        if (this.contract.priceRevision?.activated) {
          this.priceRevisionForm.controls.activated.setValue(
            this.contract.priceRevision?.activated?.map(x => this.dayEnum[x])?.filter(x => x !== undefined) || null,
          );
        }

        break;
      case RecurrenceEnum.Monthly:
        this.repeatLabel = 'Mois';
        this.autoCompleteValue = Array.from({ length: 31 }, (_, index) => index + 1);
        this.autoCompleteDisplayProperty = null;
        this.autoCompleteType = 'autocomplete';
        this.autoCompleteIsActive = true;

        this.anniversaryCheckboxActive = true;
        this.priceRevisionForm.controls.isAnniversaryDate.setValue(true);

        this.autoCompleteAnniversaryDate();

        break;
      case RecurrenceEnum.Yearly:
        this.repeatLabel = 'Année(s)';
        this.autoCompleteValue = Array.from({ length: 31 }, (_, index) => index + 1);
        this.autoCompleteDisplayProperty = null;
        this.autoCompleteType = 'autocomplete';
        this.autoCompleteIsActive = true;
        this.autocompleteMonth = true;

        if (this.contract.priceRevision?.activated) {
          this.priceRevisionForm.controls.activated.setValue(this.contract?.priceRevision?.activated);
        }

        this.anniversaryCheckboxActive = true;
        this.priceRevisionForm.controls.isAnniversaryDate.setValue(true);

        this.autoCompleteAnniversaryDate();
        break;
      default:
        this.repeatLabel = 'Jour(s)';
        this.anniversaryCheckboxActive = false;
    }
  }

  setForm(x) {
    if (x.value === RecurrenceEnum.Daily) {
      this.priceRevisionForm.get('activated').disable();
      this.autoCompleteIsActive = false;
      this.autocompleteMonth = false;
      this.displayRepeat = false;
    } else if (x.value === RecurrenceEnum.Yearly) {
      this.autocompleteMonth = true;
      this.priceRevisionForm.get('activated').enable();
      this.displayRepeat = true;
    } else {
      this.priceRevisionForm.get('activated').enable();
      this.displayRepeat = true;
      this.autocompleteMonth = false;
    }

    this.priceRevisionForm.get('repeat').setValue(1);
    this.priceRevisionForm.get('activated').setValue(null);
    this.priceRevisionForm.get('activated').setValue(null);
    if (x) {
      this.setRepeat(x.value);
    }
  }

  // Date Filter for the datepicker
  revisionStartDateFilter = (d: Date | null): boolean => {
    const contractStartDate = this.contract.startValidityDate;
    const contractEndDate = this.contract.endValidityDate;

    return moment(d).isBetween(contractStartDate, contractEndDate);
  };

  revisionEndDateFilter = (d: Date | null): boolean => {
    // end Date is between contract start date and contract end date and is after start date
    const contractStartDate = this.contract.startValidityDate;
    const contractEndDate = this.contract.endValidityDate;
    const startDate = this.priceRevisionForm.get('startDate').value;

    return moment(d).isBetween(contractStartDate, contractEndDate) && moment(d).isAfter(startDate);
  };

  isCgaNotValidated(): boolean {
    return [
      this.sustainabilityAndPriceTransForm,
      this.priceRevisionForm,
      this.deliveryCostForm,
      this.additionalCostsForm,
      this.deliveryTimeForm,
      this.interventionTimeForm,
    ].some(f => f?.invalid);
  }

  async switchToUpdateMode() {
    this.dialog.open(DialogConfirmComponent, {
      data: {
        description:
          'Êtes-vous sûr de vouloir modifier les CGA ?' +
          ' Attention cela peut avoir des conséquences sur les autres étapes.',
        cancelText: 'ANNULER',
        title: 'Modifier les CGA',
        validText: 'CONFIRMER',
        call$: this.contractService.updateStep(this.id, { creationStep: 3 }),
      },
    });
  }

  async updateStep(step: number) {
    const entity = await this.contractService.entity;

    this.contractService.updateStep(entity._id, { creationStep: step }).subscribe(res => {
      if (step === 4) {
        const targetRoute = this.contract?.supportEstablishment ? 'internalCatalog' : 'catalog';
        this.router.navigate(['/management/engagement/', entity._id, targetRoute]).then();
      }
    });
  }

  getOccurrenceText(recurringBasket: RecurringBasketModel) {
    return recurrenceEnumTranslationOccurence(recurringBasket);
  }

  setAnniversaryDate() {
    if (this.priceRevisionForm.get('isAnniversaryDate').value) {
      this.priceRevisionForm.get('activated').setValue(null);
      this.priceRevisionForm.get('month').setValue(null);
    } else {
      this.autoCompleteAnniversaryDate();
    }
  }

  autoCompleteAnniversaryDate() {
    const startDate = moment(this.contract.startValidityDate);
    const dayOfMonth: number = startDate.date();
    const monthNumber: number = startDate.month();

    // Si 29 février, changer pour le jour précédent
    if (dayOfMonth === 29 && monthNumber === 1) {
      this.priceRevisionForm.get('activated').setValue(28);
    } else {
      this.priceRevisionForm.get('activated').setValue(dayOfMonth);
    }

    this.priceRevisionForm.get('month').setValue(this.monthEnum[monthNumber]);
  }

  getDefaultOtherExpensesId() {
    this.customerGroupService.getCurrent().subscribe((x: any) => {
      this.defaultOtherExpenses = x.data.comptable.otherExpensesType;
    });
  }

  isOtherExpensesType(additionalCost: GenericAdditionalCostModel): boolean {
    return additionalCost?.typeAdditionalCosts?._id === this.defaultOtherExpenses?._id;
  }
}
